import {
  FilterGroupDto,
  PaginatedSMSMessagesDto,
  SMSMessageDto,
} from '@act/shared/data-transfer-objects';
import {
  PayloadAction,
  createAction,
  createEntityAdapter,
  createSelector,
  createSlice,
} from '@reduxjs/toolkit';
import { smsMessageRequests } from '../requests/sms-message.requests';
import { useAppSelector } from '../store-hooks';
import { SMSMessageState } from '../types/smsMessageTypes';
import { createApiThunk } from '../utils/api-thunk';
import { resetRedux } from '../actions/shared-actions';

// THUNKS/ACTIONS ==============================================================
export const getSMSMessages = createApiThunk(smsMessageRequests.getSMSMessages);
export const getSMSFilters = createApiThunk(smsMessageRequests.getSMSFilters);
export const assignSMSMessage = createApiThunk(
  smsMessageRequests.assignSMSMessage
);
export const unassignSMSMessage = createApiThunk(
  smsMessageRequests.unassignSMSMessage
);

export const unassignSMSMessageSuccess = createAction<SMSMessageDto>(
  'smsMessages/unassignSMSMessageSuccess'
);
// INITIAL STATE ===============================================================
const smsMessageAdapter = createEntityAdapter<SMSMessageDto>();
const initialState: SMSMessageState = {
  smsPageIds: [],
  smsMessages: smsMessageAdapter.getInitialState({
    status: 'initial',
    totalCount: 0,
  }),
  smsFilterOptions: null,
  smsFilterOptionStatus: 'initial',
};

// SLICE =======================================================================
export const smsMessagesSlice = createSlice({
  name: 'smsMessages',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(resetRedux, state => {
        state = { ...initialState };
        return state;
      })
      .addCase(getSMSMessages.pending, state => {
        state.smsMessages.status = 'loading';
      })
      .addCase(
        getSMSMessages.fulfilled,
        (state, action: PayloadAction<PaginatedSMSMessagesDto>) => {
          smsMessageAdapter.addMany(
            state.smsMessages,
            action.payload.smsMessages
          );
          state.smsMessages.totalCount = action.payload.count;
          state.smsPageIds = action.payload.smsMessages.map(sms => sms.id);
          state.smsMessages.status = 'success';
        }
      )
      .addCase(getSMSMessages.rejected, state => {
        state.smsMessages.status = 'failed';
      })
      .addCase(getSMSFilters.pending, state => {
        state.smsFilterOptionStatus = 'loading';
      })
      .addCase(
        getSMSFilters.fulfilled,
        (state, action: PayloadAction<FilterGroupDto>) => {
          state.smsFilterOptionStatus = 'success';
          state.smsFilterOptions = action.payload;
        }
      )
      .addCase(getSMSFilters.rejected, state => {
        state.smsFilterOptionStatus = 'failed';
      })
      .addCase(assignSMSMessage.pending, state => {
        state.smsMessages.status = 'loading';
      })
      .addCase(
        assignSMSMessage.fulfilled,
        (state, action: PayloadAction<SMSMessageDto>) => {
          smsMessageAdapter.upsertOne(state.smsMessages, action.payload);
          state.smsMessages.status = 'success';
        }
      )
      .addCase(assignSMSMessage.rejected, state => {
        state.smsMessages.status = 'failed';
      })
      .addCase(unassignSMSMessage.pending, state => {
        state.smsMessages.status = 'loading';
      })
      .addCase(
        unassignSMSMessageSuccess,
        (state, action: PayloadAction<SMSMessageDto>) => {
          smsMessageAdapter.upsertOne(state.smsMessages, action.payload);
          state.smsMessages.status = 'success';
        }
      );
  },
});

// REDUCER =====================================================================
export const smsMessagesReducer = smsMessagesSlice.reducer;

// SELECTORS ===================================================================
const selectSMSMessageSliceState = state =>
  (state as { smsMessages: typeof initialState })?.smsMessages;

const selectSMSMessagesState = createSelector(
  selectSMSMessageSliceState,
  s => s?.smsMessages
);

const selectSMSPage = createSelector(selectSMSMessageSliceState, state =>
  state.smsPageIds.map(id => state.smsMessages.entities[id])
);

export const {
  selectAll: selectSMSMessages,
  selectEntities: selectMessagesMap,
} = smsMessageAdapter.getSelectors(selectSMSMessagesState);

export const selectSMSFilters = createSelector(
  selectSMSMessageSliceState,
  s => s.smsFilterOptions
);

export const selectSMSFilterLoadingStatus = createSelector(
  selectSMSMessageSliceState,
  s => s.smsFilterOptionStatus
);

export const selectLoadingStatus = createSelector(
  selectSMSMessagesState,
  s => s?.status
);

export const selectTotalCount = createSelector(
  selectSMSMessagesState,
  s => s?.totalCount
);

// EXPORT HELPER ===============================================================
export const smsMessagesAppSelectors = {
  useSelectSMSMessages: () => useAppSelector(selectSMSMessages),
  useSelectSMSMessagesSliceStatus: () => useAppSelector(selectLoadingStatus),
  useSelectSMSMessagesPage: () => useAppSelector(selectSMSPage),
  useSelectTotalCount: () => useAppSelector(selectTotalCount),
  useSelectSMSFilters: () => useAppSelector(selectSMSFilters),
  useSelectSMSFilterLoadingStatus: () =>
    useAppSelector(selectSMSFilterLoadingStatus),
};

// EFFECTS =====================================================================
// WEBSOCKET ACTIONS ===========================================================
